import { Clickable } from '@carvana/merch-components';
import { useCallback, useEffect, useState } from 'react';
import DeliveryInfo from '@/giveaway/VehicleTile/DeliveryInfo';
import useBadge from '@/vdp/VehiclesYouMightLike/VehicleTile/useBadge';
import { blobCdnUrl, vexCdnUrl } from '~/constants/environment';
import { InventoryTypeId } from '~/constants/inventoryType';
import Store from '~/constants/store';
import { useIsLoggedIn } from '~/context/common/UserContext';
import { RecommendedVehicle } from '~/contracts/vdp';
import { useToggleFavorite } from '~/hooks/common/favorites';
import Heart from '~/icons/HeartWithBorder';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import {
  DetailsGrid,
  DetailsWrapper,
  Image,
  LowerSectionWrapper,
  Price,
  TrimAndMileage,
  Wrapper,
  YearMakeModel,
  Overlay,
  UpperSection
} from './VehicleTile.styles';

const useLabel = (vehicle: RecommendedVehicle) => {
  const is3PR = vehicle.storeKey === Store.Manheim;
  const isPartner =
    vehicle.vehicleInventoryType === InventoryTypeId.DealerMarketplace ||
    vehicle.storeKey === Store.DMP2Core;
  const isLightAccident = vehicle.storeKey === Store.LightAccident;

  switch (true) {
    case isPartner:
      return 'Partner';
    case is3PR || isLightAccident:
      return null;
    default:
      return 'Carvana Certified';
  }
};

type OwnProps = {
  vehicle: RecommendedVehicle;
};

const VehicleTile = ({ vehicle }: OwnProps) => {
  const {
    isFavorited,
    make,
    mileage,
    model,
    price: { total },
    trim,
    vehicleId,
    year
  } = vehicle;

  const imgUrl =
    vehicle.vehicleInventoryType === InventoryTypeId.DealerMarketplace
      ? `${vexCdnUrl}/${vehicle.stockNumber}/hero.jpg`
      : `${blobCdnUrl}${vehicle.imageUrl}`;
  const Badge = useBadge(false, vehicle);
  const isLoggedIn = useIsLoggedIn();
  const label = useLabel(vehicle);
  const [isFavoritedLocally, setFavoritedLocally] = useState(isFavorited);
  const toggleFavorite = useToggleFavorite();

  const handleFavorite = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      toggleFavorite({ vehicleId });
      if (isLoggedIn) setFavoritedLocally(v => !v);
    },
    [isLoggedIn, toggleFavorite, vehicleId]
  );

  useEffect(() => {
    setFavoritedLocally(isFavorited);
  }, [isFavorited, setFavoritedLocally]);

  return (
    <Clickable
      className="m-auto m:m-0"
      data-analytics-id="Vehicle Tile"
      data-inventory-type={vehicle.vehicleInventoryType}
      data-qa="vehicle-tile"
      element={Wrapper}
      href={`/vehicle/${vehicleId}`}
    >
      <UpperSection>
        <Image alt="Vehicle" src={`${imgUrl}?width=450`} />
        <Overlay className={Badge ? undefined : 'flex-col'}>
          {Badge ? <Badge /> : null}
          <Clickable
            className={Badge ? undefined : 'self-end'}
            data-analytics-id="Vehicles You Might Like Favorite"
            onClick={handleFavorite}
            title="Add to Favorites"
          >
            <Heart
              blue
              className="pointer-events-auto m-12 h-[18px] w-[20px]"
              isFavorited={isFavoritedLocally}
            />
          </Clickable>
        </Overlay>
      </UpperSection>
      <DetailsWrapper>
        <DetailsGrid>
          <span className="text-grey-3 tl-label-xs">{label || ''}</span>
          <YearMakeModel>
            {year} {make} {model}
          </YearMakeModel>
          <TrimAndMileage>
            {trim} <span className="px-4">•</span> {mileage.toLocaleString()}{' '}
            miles
          </TrimAndMileage>
        </DetailsGrid>
        <div className="mb-12 flex items-end">
          <Price>${formatAndDropDecimal(total)}</Price>
        </div>
        <div>
          <div className="border-b border-grey-2" />
          <LowerSectionWrapper>
            <DeliveryInfo vehicle={vehicle} />
          </LowerSectionWrapper>
        </div>
      </DetailsWrapper>
    </Clickable>
  );
};

export default VehicleTile;
